import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { Modal, Field, TextInput, Select, Textarea, FormActions, dbErrorMessage } from './form';

type Base<T> = { open: boolean; onClose: () => void; onSaved: () => void; initial?: T | null };
const slugify = (s: string) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 48);

// ── Campaign ─────────────────────────────────────────────────────────────────
export type CampaignRow = {
  id: string; name: string; channel: string | null; utm_source: string | null; utm_medium: string | null; utm_campaign: string | null;
  starts_on: string | null; ends_on: string | null; budget: number | null; active: boolean; notes?: string | null;
};
export function CampaignForm({ open, onClose, onSaved, initial }: Base<CampaignRow>) {
  const { t } = useTranslation();
  const { id: orgId, currency } = useOrg();
  const [f, setF] = useState({ name: '', channel: '', utm_source: '', utm_medium: '', utm_campaign: '', starts_on: '', ends_on: '', budget: '', active: true, notes: '' });
  const [err, setErr] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return; setErr({});
    setF({
      name: initial?.name ?? '', channel: initial?.channel ?? '', utm_source: initial?.utm_source ?? '', utm_medium: initial?.utm_medium ?? '', utm_campaign: initial?.utm_campaign ?? '',
      starts_on: initial?.starts_on ?? '', ends_on: initial?.ends_on ?? '', budget: initial?.budget != null ? String(initial.budget) : '', active: initial?.active ?? true, notes: initial?.notes ?? '',
    });
  }, [open, initial]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!f.name.trim()) { setErr({ name: t('form.errRequired') }); return; }
    setBusy(true);
    const payload = {
      name: f.name.trim(), channel: f.channel.trim() || null, utm_source: f.utm_source.trim() || null, utm_medium: f.utm_medium.trim() || null, utm_campaign: f.utm_campaign.trim() || null,
      starts_on: f.starts_on || null, ends_on: f.ends_on || null, budget: f.budget ? Number(f.budget) : null, currency: f.budget ? currency : null, active: f.active, notes: f.notes.trim() || null,
    };
    const res = initial?.id ? await supabase.from('campaigns').update(payload).eq('id', initial.id) : await supabase.from('campaigns').insert({ ...payload, organization_id: orgId } as any);
    setBusy(false);
    if (res.error) { setErr({ _: dbErrorMessage(t, res.error) }); return; }
    onSaved(); onClose();
  }

  return (
    <Modal open={open} onClose={onClose} title={initial?.id ? t('mkt.editCampaign') : t('mkt.newCampaign')} wide>
      <form onSubmit={submit} className="flex flex-col gap-4 p-5">
        {err._ && <div className="rounded-md bg-st-overdue-bg px-3 py-2 text-sm text-st-overdue-fg">{err._}</div>}
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('form.name')} required error={err.name}><TextInput value={f.name} error={!!err.name} onChange={(e) => setF({ ...f, name: e.target.value })} autoFocus /></Field>
          <Field label={t('mkt.channel')}><TextInput value={f.channel} onChange={(e) => setF({ ...f, channel: e.target.value })} placeholder="email, social, ads…" /></Field>
          <Field label="utm_source"><TextInput value={f.utm_source} onChange={(e) => setF({ ...f, utm_source: e.target.value })} /></Field>
          <Field label="utm_medium"><TextInput value={f.utm_medium} onChange={(e) => setF({ ...f, utm_medium: e.target.value })} /></Field>
          <Field label="utm_campaign"><TextInput value={f.utm_campaign} onChange={(e) => setF({ ...f, utm_campaign: e.target.value })} /></Field>
          <Field label={`${t('mkt.budget')} (${currency})`}><TextInput type="number" min="0" step="0.01" value={f.budget} onChange={(e) => setF({ ...f, budget: e.target.value })} /></Field>
          <Field label={t('mkt.startsOn')}><TextInput type="date" value={f.starts_on} onChange={(e) => setF({ ...f, starts_on: e.target.value })} /></Field>
          <Field label={t('mkt.endsOn')}><TextInput type="date" value={f.ends_on} onChange={(e) => setF({ ...f, ends_on: e.target.value })} /></Field>
          <Field label={t('crm.active')}>
            <Select value={f.active ? '1' : '0'} onChange={(e) => setF({ ...f, active: e.target.value === '1' })}><option value="1">{t('common.yes')}</option><option value="0">{t('common.no')}</option></Select>
          </Field>
        </div>
        <Field label={t('form.notes')}><Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field>
        <FormActions onCancel={onClose} busy={busy} />
      </form>
    </Modal>
  );
}

// ── Capture form ─────────────────────────────────────────────────────────────
export type CaptureFormRow = {
  id: string; name: string; slug: string; target_stage_id: string | null; source_id: string | null; campaign_id: string | null;
  require_consent: boolean; consent_text: string | null; redirect_url: string | null; active: boolean; submit_count?: number;
};
type Opt = { id: string; name: string };
export function CaptureFormForm({ open, onClose, onSaved, initial }: Base<CaptureFormRow>) {
  const { t } = useTranslation();
  const { id: orgId } = useOrg();
  const [stages, setStages] = useState<Opt[]>([]);
  const [sources, setSources] = useState<Opt[]>([]);
  const [campaigns, setCampaigns] = useState<Opt[]>([]);
  const [f, setF] = useState({ name: '', slug: '', target_stage_id: '', source_id: '', campaign_id: '', require_consent: true, consent_text: '', redirect_url: '', active: true, slugTouched: false });
  const [err, setErr] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return; setErr({});
    (async () => {
      const [st, sr, cp] = await Promise.all([
        supabase.from('pipeline_stages').select('id,name').is('deleted_at', null).order('position'),
        supabase.from('lead_sources').select('id,name').is('deleted_at', null).order('name'),
        supabase.from('campaigns').select('id,name').is('deleted_at', null).eq('active', true).order('name'),
      ]);
      setStages((st.data as Opt[]) ?? []); setSources((sr.data as Opt[]) ?? []); setCampaigns((cp.data as Opt[]) ?? []);
      setF({
        name: initial?.name ?? '', slug: initial?.slug ?? '', target_stage_id: initial?.target_stage_id ?? '', source_id: initial?.source_id ?? '',
        campaign_id: initial?.campaign_id ?? '', require_consent: initial?.require_consent ?? true, consent_text: initial?.consent_text ?? '',
        redirect_url: initial?.redirect_url ?? '', active: initial?.active ?? true, slugTouched: !!initial?.slug,
      });
    })();
  }, [open, initial]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const er: Record<string, string> = {};
    if (!f.name.trim()) er.name = t('form.errRequired');
    const slug = (f.slug || slugify(f.name));
    if (!slug) er.slug = t('form.errRequired');
    setErr(er); if (Object.keys(er).length) return;
    setBusy(true);
    const payload = {
      name: f.name.trim(), slug, target_stage_id: f.target_stage_id || null, source_id: f.source_id || null, campaign_id: f.campaign_id || null,
      require_consent: f.require_consent, consent_text: f.consent_text.trim() || null, redirect_url: f.redirect_url.trim() || null, active: f.active,
    };
    const res = initial?.id ? await supabase.from('capture_forms').update(payload).eq('id', initial.id) : await supabase.from('capture_forms').insert({ ...payload, organization_id: orgId } as any);
    setBusy(false);
    if (res.error) { setErr({ _: dbErrorMessage(t, res.error) }); return; }
    onSaved(); onClose();
  }

  return (
    <Modal open={open} onClose={onClose} title={initial?.id ? t('mkt.editForm') : t('mkt.newForm')} wide>
      <form onSubmit={submit} className="flex flex-col gap-4 p-5">
        {err._ && <div className="rounded-md bg-st-overdue-bg px-3 py-2 text-sm text-st-overdue-fg">{err._}</div>}
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('form.name')} required error={err.name}><TextInput value={f.name} error={!!err.name} onChange={(e) => setF({ ...f, name: e.target.value, slug: f.slugTouched ? f.slug : slugify(e.target.value) })} autoFocus /></Field>
          <Field label={t('mkt.slug')} required error={err.slug} hint={t('mkt.slugHint')}><TextInput value={f.slug} error={!!err.slug} onChange={(e) => setF({ ...f, slug: slugify(e.target.value), slugTouched: true })} /></Field>
          <Field label={t('crm.stage')}>
            <Select value={f.target_stage_id} onChange={(e) => setF({ ...f, target_stage_id: e.target.value })}><option value="">—</option>{stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</Select>
          </Field>
          <Field label={t('crm.source')}>
            <Select value={f.source_id} onChange={(e) => setF({ ...f, source_id: e.target.value })}><option value="">—</option>{sources.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</Select>
          </Field>
          <Field label={t('nav.marketing')}>
            <Select value={f.campaign_id} onChange={(e) => setF({ ...f, campaign_id: e.target.value })}><option value="">—</option>{campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</Select>
          </Field>
          <Field label={t('mkt.requireConsent')}>
            <Select value={f.require_consent ? '1' : '0'} onChange={(e) => setF({ ...f, require_consent: e.target.value === '1' })}><option value="1">{t('common.yes')}</option><option value="0">{t('common.no')}</option></Select>
          </Field>
          <Field label={t('mkt.redirectUrl')}><TextInput value={f.redirect_url} onChange={(e) => setF({ ...f, redirect_url: e.target.value })} placeholder="https://…" /></Field>
          <Field label={t('crm.active')}>
            <Select value={f.active ? '1' : '0'} onChange={(e) => setF({ ...f, active: e.target.value === '1' })}><option value="1">{t('common.yes')}</option><option value="0">{t('common.no')}</option></Select>
          </Field>
        </div>
        <Field label={t('mkt.consentText')}><Textarea value={f.consent_text} onChange={(e) => setF({ ...f, consent_text: e.target.value })} placeholder={t('mkt.consentPlaceholder')} /></Field>
        <FormActions onCancel={onClose} busy={busy} />
      </form>
    </Modal>
  );
}
